import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { DateRange } from '@angular/material/datepicker';
import { map, Subject, tap } from 'rxjs';
import { endOfDay, endOfTomorrow, startOfToday, startOfTomorrow } from 'date-fns';
import {
  CalendarRangePickerComponent,
  createActionState,
  FormField,
  formValueMap,
  minDateValidator,
} from '@at/shared';
import { DatesApi, PostRangeRequest } from '../../../api';
import { WeekdaysPickerComponent } from '../../weekdays-picker/weekdays-picker.component';
import { weekRangeDurationValidator } from '../../../validators/week-range-duration.validator';
import { calendarRangeDurationValidator } from '../../../validators/calendar-range-duration.validator';
import { DatesService } from '../../../services/dates.service';
import { RouterLink } from '@angular/router';

@Component({
  selector: 'at-range-selector',
  standalone: true,
  imports: [
    CalendarRangePickerComponent,
    ReactiveFormsModule,
    FormField,
    WeekdaysPickerComponent,
    AsyncPipe,
    RouterLink,
  ],
  templateUrl: './range-selector.component.html',
  styleUrl: './range-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeSelectorComponent {
  #datesApi = inject(DatesApi);
  #datesService = inject(DatesService);

  protected minStartDate = signal<Date>(new Date());
  protected saveCriteria = new Subject<boolean>();
  protected rangeForm = new FormGroup(
    {
      start: new FormControl<Date | null>(null, {
        validators: [minDateValidator(startOfToday())],
      }),
      end: new FormControl<Date | null>(null),
      minDuration: new FormControl<number>(1, {
        validators: [Validators.required, Validators.min(1)],
        nonNullable: true,
      }),
      weekdays: new FormControl<number[] | null>(null),
      notifyMembers: new FormControl<boolean>(false, { nonNullable: true }),
    },
    [
      weekRangeDurationValidator('minDuration', 'weekdays'),
      calendarRangeDurationValidator('minDuration', 'start', 'end'),
    ],
  );
  protected initialDates = signal<DateRange<Date> | undefined>(undefined);
  protected isEditing = signal(false);
  protected moduleRootPath = this.#datesService.getModuleRootPath();

  protected state = createActionState({
    trigger: this.saveCriteria.pipe(
      tap((notify) => this.rangeForm.patchValue({ notifyMembers: notify })),
      formValueMap(this.rangeForm),
      map(
        (formValue) =>
          ({
            ...formValue,
            start: formValue.start!,
            end: formValue.end ? endOfDay(formValue.end) : endOfDay(formValue.start!),
          }) satisfies PostRangeRequest,
      ),
    ),
    beforeStart: () => {
      this.rangeForm.disable();
    },
    apiCall: (request) => this.#datesApi.postRange(request),
    onError: () => {
      this.rangeForm.enable();
    },
    onSucceed: () => {
      this.rangeForm.enable();
      this.#datesService.updateDatesSummary(true);
    },
  });

  protected save = (notify = false) => {
    this.saveCriteria.next(notify);
  };

  constructor() {
    effect(() => {
      const summary = this.#datesService.datesSummary();
      const dates = summary?.data?.rangeCriteria;
      if (dates) {
        untracked(() => {
          this.rangeForm.patchValue(dates);
          this.initialDates.set(new DateRange(dates.start, dates.end));
          this.isEditing.set(true);
        });
      } else {
        untracked(() => {
          this.initialDates.set(new DateRange(startOfTomorrow(), endOfTomorrow()));
        });
      }
    });
  }
}
